import { Check, RotateCcw } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { useAppContext } from "@/hooks/useAppContext";
import { cn } from "@/lib/utils";

interface ColorOption {
  name: string;
  hsl: string;
  tailwindClass: string;
}

const colorOptions: ColorOption[] = [
  { name: 'Violet', hsl: '271 81% 56%', tailwindClass: 'bg-violet-500' },
  { name: 'Purple', hsl: '270 70% 50%', tailwindClass: 'bg-purple-500' },
  { name: 'Fuchsia', hsl: '292 84% 61%', tailwindClass: 'bg-fuchsia-500' },
  { name: 'Pink', hsl: '330 81% 60%', tailwindClass: 'bg-pink-500' },
  { name: 'Rose', hsl: '350 89% 60%', tailwindClass: 'bg-rose-500' },
  { name: 'Red', hsl: '0 84% 60%', tailwindClass: 'bg-red-500' },
  { name: 'Orange', hsl: '25 95% 53%', tailwindClass: 'bg-orange-500' },
  { name: 'Amber', hsl: '38 92% 50%', tailwindClass: 'bg-amber-500' },
  { name: 'Yellow', hsl: '48 96% 53%', tailwindClass: 'bg-yellow-500' },
  { name: 'Lime', hsl: '84 85% 43%', tailwindClass: 'bg-lime-500' },
  { name: 'Green', hsl: '142 71% 45%', tailwindClass: 'bg-green-500' },
  { name: 'Emerald', hsl: '160 84% 39%', tailwindClass: 'bg-emerald-500' },
  { name: 'Teal', hsl: '168 84% 39%', tailwindClass: 'bg-teal-500' },
  { name: 'Cyan', hsl: '190 95% 39%', tailwindClass: 'bg-cyan-500' },
  { name: 'Sky', hsl: '199 89% 48%', tailwindClass: 'bg-sky-500' },
  { name: 'Blue', hsl: '217 91% 60%', tailwindClass: 'bg-blue-500' },
  { name: 'Indigo', hsl: '239 84% 67%', tailwindClass: 'bg-indigo-500' },
  { name: 'Slate', hsl: '215 16% 47%', tailwindClass: 'bg-slate-500' },
];

export function PrimaryColorPicker() {
  const { t } = useTranslation();
  const { config, updateConfig } = useAppContext();

  const setPrimaryColor = (hsl: string | undefined) => {
    updateConfig((currentConfig) => ({
      ...currentConfig,
      primaryColor: hsl,
    }));
  };

  const currentColor = config.primaryColor;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {colorOptions.map((color) => {
          const isSelected = currentColor === color.hsl;
          return (
            <button
              key={color.name}
              onClick={() => setPrimaryColor(color.hsl)}
              className={cn(
                "relative w-8 h-8 rounded-full transition-all duration-150",
                color.tailwindClass,
                "hover:scale-110 hover:ring-2 hover:ring-offset-2 hover:ring-offset-background",
                isSelected && "ring-2 ring-offset-2 ring-offset-background ring-foreground"
              )}
              title={color.name}
              aria-label={color.name}
            >
              {isSelected && (
                <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-sm" />
              )}
            </button>
          );
        })}
      </div>

      {currentColor && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPrimaryColor(undefined)}
          className="h-8 text-xs text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-3 w-3 mr-1.5" />
          {t('resetToDefault')}
        </Button>
      )}
    </div>
  );
}
